import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import { useU3204ConversationHandlers } from '../hooks/useU3204ConversationHandlers';
import { useU3204SendMessageHandlers } from '../hooks/useU3204SendMessageHandlers';
import { u3204ComputeConversationTokenUsage } from '../utils/u3204ComputeConversationTokenUsage';
import { u3204BuildExportTokenDisplay } from '../utils/u3204ExportMessageDisplay';
import { u3204PickLocalizedDefaultPrompt } from '../utils/u3204PickLocalizedDefaultPrompt';
import { u3204IsScrolledToBottom, u3204ScrollToBottom } from '../utils/u3204ScrollUtils';
import U3204MessageBubble from './U3204MessageBubble';

const U3204MessageList: React.FC = () => {
  const { t, i18n } = useTranslation(NS);
  const {
    messages,
    loadingMessages,
    isNewChat,
    streamState,
    selectedMissionId,
    accessibleMissions,
    activeMission,
    activeConversationId,
  } = useU3204Context();
  const { createConversation } = useU3204ConversationHandlers();
  const { sendMessage } = useU3204SendMessageHandlers();
  const [suggestionSending, setSuggestionSending] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const tokenUsage = useMemo(() => u3204ComputeConversationTokenUsage(messages), [messages]);
  const exportTokenDisplay = useMemo(
    () => u3204BuildExportTokenDisplay(messages, tokenUsage.outputTokensByAssistantMessageId),
    [messages, tokenUsage.outputTokensByAssistantMessageId],
  );

  const defaultPrompts = useMemo(() => {
    const missionId = selectedMissionId ?? activeMission?.id;
    if (!missionId) return [];
    const fromAccessible = accessibleMissions.find((mission) => mission.id === missionId);
    if (fromAccessible) {
      return [...(fromAccessible.defaultPrompts ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return [...(activeMission?.defaultPrompts ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [accessibleMissions, activeMission?.defaultPrompts, activeMission?.id, selectedMissionId]);

  const showHelpSuggestions = defaultPrompts.length > 0;

  const handleSuggestionClick = useCallback(
    async (prompt: string) => {
      if (suggestionSending || streamState === 'streaming') return;
      const missionId = selectedMissionId ?? activeMission?.id;
      if (!missionId) return;

      setSuggestionSending(true);
      try {
        let conversationId = activeConversationId;
        if (!conversationId) {
          const created = await createConversation(missionId);
          conversationId = created?.id ?? null;
        }
        if (!conversationId) return;
        await sendMessage(prompt, [], conversationId);
      } finally {
        setSuggestionSending(false);
      }
    },
    [
      activeConversationId,
      activeMission?.id,
      createConversation,
      selectedMissionId,
      sendMessage,
      streamState,
      suggestionSending,
    ],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      shouldAutoScrollRef.current = u3204IsScrolledToBottom(container);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !shouldAutoScrollRef.current) return;
    // Toujours "auto" pendant le stream pour éviter les à-coups de scrollbar liés au smooth.
    u3204ScrollToBottom(container, 'auto');
  }, [messages, streamState]);

  if (loadingMessages) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isNewChat && messages.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center text-muted-foreground">
        {showHelpSuggestions ? (
          <>
            <h2 className="text-lg font-medium text-foreground">{t('help.emptyTitle', { ns: NS })}</h2>
            <div className="flex max-w-lg flex-wrap items-center justify-center gap-2">
              {defaultPrompts.map((item) => {
                const { label, promptText } = u3204PickLocalizedDefaultPrompt(item, i18n.language);
                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={suggestionSending || streamState === 'streaming'}
                    onClick={() => void handleSuggestionClick(promptText)}
                    aria-label={label}
                    className="rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-medium text-foreground">{t('emptyStateTitle', { ns: NS })}</h2>
            <p className="max-w-md text-sm">{t('emptyState', { ns: NS })}</p>
          </>
        )}
      </div>
    );
  }

  return (
    /*
     * Conteneur scroll : ne PAS mettre display:flex ici.
     * flex + min-h-full + justify-end empêche scrollHeight de dépasser clientHeight
     * (conversation non scrollable). Pattern : block scroll → inner min-h-full flex justify-end.
     */
    <div
      ref={containerRef}
      className="bg-page min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-6 sm:px-5"
    >
      <div className="flex min-h-full flex-col justify-end gap-5">
        {messages.map((message) => {
          let tokenCount: number | undefined;
          let tokenVariant: 'input' | 'output' | undefined;

          if (message.role === 'user') {
            tokenCount = tokenUsage.inputTokensByUserMessageId.get(message.id);
            tokenVariant = tokenCount != null ? 'input' : undefined;
          } else if (message.role === 'assistant' && message.generationStatus === 'completed') {
            if (exportTokenDisplay.hideOutputOnMessageIds.has(message.id)) {
              tokenCount = undefined;
              tokenVariant = undefined;
            } else if (exportTokenDisplay.outputTokensByExportMessageId.has(message.id)) {
              tokenCount = exportTokenDisplay.outputTokensByExportMessageId.get(message.id);
              tokenVariant = 'output';
            } else {
              tokenCount = tokenUsage.outputTokensByAssistantMessageId.get(message.id);
              tokenVariant = tokenCount != null ? 'output' : undefined;
            }
          }

          return (
            <U3204MessageBubble
              key={message.id}
              message={message}
              tokenCount={tokenCount}
              tokenVariant={tokenVariant}
            />
          );
        })}
      </div>
    </div>
  );
};

export default U3204MessageList;
